import React from 'react';
import { useTheme } from '../../ThemeProvider/ThemeProvider';

export interface ActivityIndicatorProps {
  /**
   * Size of the activity indicator
   */
  size?: 'sm' | 'md' | 'lg';
}

const sizeMap = {
  sm: 1,
  md: 1.5,
  lg: 2.5,
};

export function ActivityIndicator({
  size = 'md',
  ...props
}: ActivityIndicatorProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof ActivityIndicatorProps>) {
  const { tokens } = useTheme();

  return (
    <div css={{ height: `${sizeMap[size] * 3}rem`, width: `${sizeMap[size] * 3}rem`, position: 'relative' }} {...props}>
      <div
        css={{
          '@keyframes top-box-animation': {
            '0%, 100%': { transform: 'translate(0, 0)' },
            '12.5%': { transform: `translate(-${sizeMap[size]}rem, 0)` },
            '24.99%': { backgroundColor: tokens.colors['bosch-red-50'], transform: 'translate(0, 0)' },
            '25%': { backgroundColor: tokens.colors['bosch-blue-50'] },
            '37.5%': { transform: `translate(0, -${sizeMap[size]}rem)` },
            '50%': { transform: 'translate(0, 0)' },
            '62.5%': { transform: `translate(-${sizeMap[size]}rem, 0)` },
            '74.99%': { backgroundColor: tokens.colors['bosch-blue-50'], transform: 'translate(0, 0)' },
            '75%': { backgroundColor: tokens.colors['bosch-red-50'] },
            '87.5%': { transform: `translate(0, -${sizeMap[size]}rem)` },
          },
          animationName: 'top-box-animation',
          animationDuration: '3s',
          animationTimingFunction: 'ease-in-out',
          animationDelay: '0s',
          animationIterationCount: 'infinite',
          animationFillMode: 'none',
          backgroundColor: tokens.colors['bosch-red-50'],
          position: 'absolute',
          height: `calc(100% / 3)`,
          width: `calc(100% / 3)`,
          top: `calc(100% / 3)`,
          left: `calc(100% / 3)`,
          zIndex: 1,
        }}
      />
      <div
        css={{
          '@keyframes bottom-box-animation': {
            '0%, 100%': { transform: 'translate(0, 0)', backgroundColor: tokens.colors['bosch-purple-50'] },
            '12.5%': { transform: `translate(${sizeMap[size]}rem, 0)`, zIndex: 2 },
            '25%': { transform: 'translate(0, 0)', zIndex: 2 },
            '37.5%': { transform: `translate(0, ${sizeMap[size]}rem)`, zIndex: 0 },
            '49.99%': { transform: 'translate(0, 0)', zIndex: 0, backgroundColor: tokens.colors['bosch-purple-50'] },
            '50%': { backgroundColor: tokens.colors['bosch-green-50'] },
            '62.5%': { transform: `translate(${sizeMap[size]}rem, 0)`, zIndex: 2 },
            '75%': { transform: 'translate(0, 0)', zIndex: 2 },
            '87.5%': { transform: `translate(0, ${sizeMap[size]}rem)`, zIndex: 0 },
            '99.99%': { backgroundColor: tokens.colors['bosch-green-50'] },
          },
          animationName: 'bottom-box-animation',
          animationDuration: '3s',
          animationTimingFunction: 'ease-in-out',
          animationDelay: '0s',
          animationIterationCount: 'infinite',
          animationFillMode: 'none',
          backgroundColor: tokens.colors['bosch-purple-50'],
          position: 'absolute',
          height: `calc(100% / 3)`,
          width: `calc(100% / 3)`,
          top: `calc(100% / 3)`,
          left: `calc(100% / 3)`,
          zIndex: 0,
        }}
      />
    </div>
  );
}
